// Plan 17-07 Task 2 — About re-skin (POLISH-13).
//
// Prototype layout: 52px display-word RUNEGAMBIT + version Chip + credits +
// SCOWL + SALDO Attribution (DEC-020 lock — preserved verbatim) + 3 link
// Btns to /privacy /legal/terms /legal/licenses.
//
// Existing useRouter + useDocumentTitle + Attribution preserved. Visual
// literals tokenized.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { useRouter } from '../../router/router.js'
import { Btn } from '../components/Btn.js'
import { Chip } from '../components/Chip.js'
import { Screen, ScreenHeader } from '../components/Screen.js'
import { Attribution } from './Attribution.js'

function buildVersion(): string {
  try {
    return import.meta.env?.VITE_BUILD_VERSION ?? 'dev'
  } catch {
    return 'dev'
  }
}

export function AboutScreen(): JSX.Element {
  useDocumentTitle('doc.title.about')
  const router = useRouter()
  const intl = useIntl()
  return (
    <Screen
      name="about"
      gutter={22}
      contentStyle={{
        gap: 'var(--space-5)',
        alignItems: 'center',
        textAlign: 'center',
      }}
      header={
        <ScreenHeader
          title={<FormattedMessage id="about.title" defaultMessage="About" />}
          onBack={() => router.navigate('/settings')}
          backLabel={intl.formatMessage({
            id: 'nav.back',
            defaultMessage: 'Back',
          })}
        />
      }
    >
      <Chip variant="them">
        <FormattedMessage
          id="about.version"
          defaultMessage="Version {version}"
          values={{ version: buildVersion() }}
        />
      </Chip>

      <section
        className="card"
        aria-label={intl.formatMessage({ id: 'about.section.credits_aria' })}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          textAlign: 'left',
        }}
      >
        <p
          style={{
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-15, 15px)',
            lineHeight: 1.6,
            margin: 0,
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id="about.credits"
            defaultMessage="Runegambit by Arcmill. Built on a single OpenAPI 3.1 contract; backend on Cloudflare Workers."
          />
        </p>
        {/* Folk parity: a FolkRule divider (centred folk-star motif flanked
            by hairlines) replaces the plain hairline, per the prototype's
            About <FolkRule>. Decorative — aria-hidden. */}
        <div
          className="folk-rule"
          aria-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-2)',
            color: 'var(--color-ink-ghost)',
            margin: 'var(--space-2) 0',
          }}
        >
          <span
            style={{
              flex: 1,
              height: '2px',
              borderRadius: '2px',
              background: 'currentColor',
              opacity: 0.5,
            }}
          />
          <svg
            width="22"
            height="14"
            viewBox="0 0 22 14"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M11 2c.6 3 1.4 3.8 4 4-2.6.6-3.4 1.4-4 4-.6-2.6-1.4-3.4-4-4 2.6-.2 3.4-1 4-4z"
              fill="currentColor"
            />
          </svg>
          <span
            style={{
              flex: 1,
              height: '2px',
              borderRadius: '2px',
              background: 'currentColor',
              opacity: 0.5,
            }}
          />
        </div>
        <Attribution opacity={1} />
      </section>

      <section
        aria-label={intl.formatMessage({ id: 'about.section.privacy_aria' })}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        <Btn
          kind="ghost"
          size="md"
          block
          onClick={() => router.navigate('/privacy')}
          testId="about-privacy-link"
        >
          <FormattedMessage
            id="about.privacy_link"
            defaultMessage="Privacy policy"
          />
        </Btn>
      </section>
    </Screen>
  )
}
